import { IconButton, RadioGroup } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AffineCommandRegistry } from '../../../../commands/registry/registry';
import {
  RIGHT_SIDEBAR_NAVIGATION_OWNER,
  rightSidebarTabDefinitionId,
} from '../../../streamlain/copilot/right-sidebar-navigation';
import { UiNavigationItem } from '../../../streamlain/copilot/ui-navigation-item';
import { navigationActionId } from '../../../streamlain/copilot/workspace-navigation-catalog';
import { WorkspaceViewContextService } from '../../../streamlain/copilot/workspace-view-context';
import { ViewService } from '../../services/view';
import { ViewSidebarTabIconTarget } from '../view-islands';
import * as styles from './sidebar-header-switcher.css';

const DRAG_SCROLL_THRESHOLD_PX = 3;

// provide a switcher for active extensions
// will be used in global top header (MacOS) or sidebar (Windows)
export const SidebarHeaderSwitcher = () => {
  const t = useI18n();
  const view = useService(ViewService).view;
  const context = useService(WorkspaceViewContextService);
  useLiveData(AffineCommandRegistry.changes$);
  const scope = useMemo(
    () => ({ workspaceId: context.workspaceId, viewId: view.id }),
    [context, view]
  );
  const tabs = useLiveData(view.sidebarTabs$);
  const activeTab = useLiveData(view.activeSidebarTab$);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    moved: boolean;
    pointerId: number | null;
    scrollLeft: number;
    startX: number;
  }>({
    moved: false,
    pointerId: null,
    scrollLeft: 0,
    startX: 0,
  });
  const suppressNextClickRef = useRef(false);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const [dragging, setDragging] = useState(false);

  const tabItems = tabs.flatMap(tab => {
    const label = tab.label ?? tab.id;
    const definition = AffineCommandRegistry.commands.get(
      navigationActionId(
        scope,
        RIGHT_SIDEBAR_NAVIGATION_OWNER,
        rightSidebarTabDefinitionId(tab.id)
      )
    )?.navigation?.definition;
    if (!definition) return [];
    return [
      {
        value: tab.id,
        renderButton: (button: React.ReactElement) => (
          <UiNavigationItem
            key={tab.id}
            scope={scope}
            ownerId={RIGHT_SIDEBAR_NAVIGATION_OWNER}
            definition={definition}
          >
            {button}
          </UiNavigationItem>
        ),
        label: (
          <ViewSidebarTabIconTarget
            className={styles.iconContainer}
            viewId={view.id}
            tabId={tab.id}
          />
        ),
        testId: `sidebar-tab-${tab.id}`,
        style: { padding: 0, fontSize: 20, width: 24 },
        attrs: { 'aria-label': label },
        tooltip: label,
        tooltipSide: 'bottom' as const,
      },
    ];
  });

  const scrollByPage = useCallback((direction: -1 | 1) => {
    const element = scrollAreaRef.current;
    if (!element) return;
    // Page by ~70% of the visible width so a click always reveals fresh tabs
    // while keeping one for context; clamp to a sane minimum on tiny widths.
    const amount = Math.max(element.clientWidth * 0.7, 96);
    element.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }, []);

  const updateScrollState = useCallback(() => {
    const element = scrollAreaRef.current;
    if (!element) return;
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    setScrollState({
      canScrollLeft: element.scrollLeft > 1,
      canScrollRight: element.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  const stopDragging = useCallback((element: HTMLDivElement) => {
    const dragState = dragStateRef.current;
    if (
      dragState.pointerId !== null &&
      element.hasPointerCapture(dragState.pointerId)
    ) {
      element.releasePointerCapture(dragState.pointerId);
    }
    suppressNextClickRef.current = dragState.moved;
    dragStateRef.current = {
      moved: false,
      pointerId: null,
      scrollLeft: 0,
      startX: 0,
    };
    setDragging(false);
    if (suppressNextClickRef.current) {
      window.setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    }
  }, []);

  const handleScrollPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (event.button !== 0 || element.scrollWidth <= element.clientWidth) {
        return;
      }

      dragStateRef.current = {
        moved: false,
        pointerId: event.pointerId,
        scrollLeft: element.scrollLeft,
        startX: event.clientX,
      };
    },
    []
  );

  const handleScrollPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      if (dragState.pointerId !== event.pointerId) return;
      if ((event.buttons & 1) === 0) {
        stopDragging(event.currentTarget);
        return;
      }

      const element = event.currentTarget;
      const deltaX = event.clientX - dragState.startX;
      if (!dragState.moved && Math.abs(deltaX) > DRAG_SCROLL_THRESHOLD_PX) {
        // Capture only a real drag. Capturing on pointerdown retargets an
        // ordinary tab click to this scroll area instead of its radio button.
        dragState.moved = true;
        element.setPointerCapture(event.pointerId);
        setDragging(true);
      }
      if (!dragState.moved) return;

      event.preventDefault();
      element.scrollLeft = dragState.scrollLeft - deltaX;
      updateScrollState();
    },
    [stopDragging, updateScrollState]
  );

  const handleScrollPointerEnd = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (dragStateRef.current.pointerId !== event.pointerId) return;
      stopDragging(event.currentTarget);
    },
    [stopDragging]
  );

  const handleScrollClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!suppressNextClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
    },
    []
  );

  useEffect(() => {
    const element = scrollAreaRef.current;
    if (!element) return;
    updateScrollState();
    element.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    return () => {
      element.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [tabs.length, updateScrollState]);

  // Buttons appear together once the strip overflows, then disable per-edge as
  // you reach it — no layout shift while scrolling within an overflowing strip.
  const overflowing = scrollState.canScrollLeft || scrollState.canScrollRight;

  return tabItems.length ? (
    <div className={styles.scrollFrame}>
      {overflowing ? (
        <IconButton
          className={styles.scrollButton}
          size="16"
          disabled={!scrollState.canScrollLeft}
          onClick={() => scrollByPage(-1)}
          tooltip={t['com.streamlain.rightSidebar.scroll-tabs-left']()}
          tooltipOptions={{ side: 'bottom' }}
          aria-label={t['com.streamlain.rightSidebar.scroll-tabs-left']()}
        >
          <ArrowLeftSmallIcon />
        </IconButton>
      ) : null}
      <div
        ref={scrollAreaRef}
        className={styles.scrollArea}
        data-dragging={dragging}
        onClickCapture={handleScrollClickCapture}
        onPointerCancel={handleScrollPointerEnd}
        onPointerDown={handleScrollPointerDown}
        onPointerMove={handleScrollPointerMove}
        onPointerUp={handleScrollPointerEnd}
      >
        <RadioGroup
          className={styles.switcher}
          iconMode
          borderRadius={8}
          itemHeight={24}
          padding={4}
          gap={8}
          items={tabItems}
          value={activeTab?.id}
        />
      </div>
      {overflowing ? (
        <IconButton
          className={styles.scrollButton}
          size="16"
          disabled={!scrollState.canScrollRight}
          onClick={() => scrollByPage(1)}
          tooltip={t['com.streamlain.rightSidebar.scroll-tabs-right']()}
          tooltipOptions={{ side: 'bottom' }}
          aria-label={t['com.streamlain.rightSidebar.scroll-tabs-right']()}
        >
          <ArrowRightSmallIcon />
        </IconButton>
      ) : null}
    </div>
  ) : null;
};
